import { create } from 'zustand';
import { loadStarterSkin, peekStarterSkin } from '@/characters/starterSkin';
import type { ColorType, ClimberPose, TeamMode, TeamSide, TeamRole } from '@shared/types';
import type {
  EquippedSet,
  CharacterSkin,
  MatchFound,
  RankProgressionPayload,
  TeamMatchFound,
  TeamMatchStep,
  TeamMatchFall,
  TeamSkillFired,
  TeamMatchOver,
  TeamMember,
} from '@shared/protocol';
import {
  STEPS_TOTAL,
  SKILL_DISRUPT_CD_S,
  SKILL_SHIELD_CD_S,
  SHIELD_DURATION_MS,
  DISRUPT_VORTEX_MS,
  DISRUPT_RESHUFFLE_COUNT,
  DISRUPT_FLASH_MS,
  WRONG_TAP_FLASH_MS,
  WRONG_TAP_POSE_RESET_MS,
  CORRECT_TAP_POSE_RESET_MS,
  ALT_SHIELD_COMBO,
  ALT_DISRUPT_COMBO,
  SPLIT_SKILL_COMBO,
  TEAM_SHIELD_BONUS_MULT,
} from '@shared/gameConfig';
import { randomSeq, genPath } from '@shared/pathGen';
import { preloadCharacterTextures } from '@/game/entities/Climber';
import type { FriendChallengeResult, FriendChallengeRun } from '@/challenges/challengeApi';

export type MatchOver = 'win' | 'lose' | null;
export type PlayMode = 'offline' | 'online';
export type MatchmakingPhase = 'idle' | 'search' | 'reveal' | 'countdown' | 'racing';

export interface FloatText {
  id: number;
  text: string;
  color: string;
  side: 'left' | 'right';
}

export interface BurstFx {
  id: number;
  color: ColorType;
  side: 'left' | 'right';
}

export interface ServerMatchSummary {
  winnerId: string;
  finalSteps: { you: number; rival: number };
  durationMs: number;
  /** Real per-player stats from the server tap log (1v1). */
  stats?: {
    you: { accuracy: number; maxCombo: number; timeMs: number };
    rival: { accuracy: number; maxCombo: number; timeMs: number };
  };
  mmrDelta: { you: number; rival: number };
  rewards: { coins: number; xp: number };
  rankProgression: RankProgressionPayload | null;
  winnerShout: { text: string; rarity: 'common' | 'rare' | 'epic' | 'legendary' } | null;
  /** All winning-team shouts (2v2). Loser screen cycles through them. */
  winnerShouts: Array<{ text: string; rarity: 'common' | 'rare' | 'epic' | 'legendary' }>;
}

type MatchSource = 'queue' | 'room' | 'local' | 'war' | 'challenge' | null;

interface MatchState {
  // ── 2v2 team match ──
  // True once a team match is found. `teamPending` is set at lobby-start so the
  // matchmaking/match screens know a TEAM_MATCH_FOUND (not solo MATCH_FOUND) is
  // coming and skip the solo QUEUE_JOIN.
  isTeam: boolean;
  teamPending: TeamMode | null;
  teamMode: TeamMode | null;
  yourSide: TeamSide | null;
  /** Whose turn it is to tap (alternating / split-color). null = no gate. */
  turnPlayerId: string | null;
  myRole: TeamRole | null;
  myOwnedColors: ColorType[] | null;
  yourTeam: TeamMember[];
  rivalTeam: TeamMember[];
  /** Combo-gated skill availability (recomputed from `combo` + mode on step). */
  shieldUnlocked: boolean;
  disruptUnlocked: boolean;

  // Online identity
  mode: PlayMode;
  matchId: string | null;
  /** Where the current match came from. Drives ResultScreen's primary-action
   * button: room-spawned matches show "BACK TO ROOM", queue matches show
   * "REMATCH". Persists between matchmaking → match → result. */
  matchSource: MatchSource;
  /** Active clan-war id when this match is a war attack. Durable war marker —
   * `matchSource` can get cleared by matchmaking effect churn, so war detection
   * keys off this instead. Survives matchmaking → match → result; cleared on
   * reset() or when the war flow is explicitly abandoned. */
  warId: string | null;
  challengeRun: FriendChallengeRun | null;
  challengeResult: FriendChallengeResult | null;
  youId: string | null;
  rivalId: string | null;

  // Matchmaking phase machine
  matchmakingPhase: MatchmakingPhase;
  countdownAt: 3 | 2 | 1 | 0 | 'GO' | null;

  // Rival display (set from server in online mode; falls back to placeholder)
  rivalName: string;
  rivalMmr: number;
  /** Rival's uploaded avatar photo (null → fall back to equipped character). */
  rivalAvatarUrl: string | null;

  // Equipped cosmetics per side (server-resolved in online mode; pulled from
  // the local authStore for offline play).
  yourEquipped: EquippedSet;
  rivalEquipped: EquippedSet;
  // Equipped Character skin per side (pose PNGs + win-effect tuning).
  yourCharacter: CharacterSkin | null;
  rivalCharacter: CharacterSkin | null;
  // Clan badge per side (tag + emblem) — shown on the in-match lane headers.
  yourClanTag: string | null;
  yourClanEmblem: string | null;
  rivalClanTag: string | null;
  rivalClanEmblem: string | null;

  // Sequence + path (server-authoritative when online)
  yourSeq: ColorType[];
  rivalSeq: ColorType[];
  yourPath: number[];
  rivalPath: number[];

  // Progress
  yourStep: number;
  rivalStep: number;
  yourPose: ClimberPose;
  rivalPose: ClimberPose;
  combo: number;
  maxCombo: number;

  // Clock
  timer: number;
  raceStartMs: number | null;
  paused: boolean;
  over: MatchOver;

  // Server-issued summary (mmr/coins/xp). Null in offline/AI mode.
  serverSummary: ServerMatchSummary | null;

  // Skill + fx
  shield: boolean;
  shieldCd: number;
  vortex: boolean;
  disruptCd: number;
  // Indices of holds about to be reshuffled by a Disrupt. The scene strobes
  // these for DISRUPT_FLASH_MS before the seq actually swaps. `your*` = an
  // opponent cast on me; `rival*` = I cast on the opponent.
  yourDisruptingIndices: number[];
  rivalDisruptingIndices: number[];
  flashWrong: boolean;
  pressedColor: ColorType | null;
  floaties: FloatText[];
  bursts: BurstFx[];

  // ── Local-only mutators ──
  reset: () => void;
  startLocalMatch: (opts?: {
    seed?: number;
    rivalName?: string;
    rivalMmr?: number;
    yourEquipped?: EquippedSet;
  }) => void;
  startChallengeMatch: (run: FriendChallengeRun, youId: string) => void;
  setChallengeResult: (result: FriendChallengeResult | null) => void;
  tick: (dt: number) => void;
  setPaused: (v: boolean) => void;
  setPressedColor: (c: ColorType | null) => void;

  tap: (color: ColorType) => 'correct' | 'wrong' | 'shielded' | 'noop';
  rivalAdvance: () => void;

  fireDisrupt: () => boolean;
  fireShield: () => boolean;
  clearShield: () => void;
  clearVortex: () => void;
  clearFlashWrong: () => void;

  pushFloat: (text: string, color: string, side: 'left' | 'right') => void;
  popFloat: (id: number) => void;
  pushBurst: (color: ColorType, side: 'left' | 'right') => void;
  popBurst: (id: number) => void;

  setYourPose: (p: ClimberPose) => void;
  setRivalPose: (p: ClimberPose) => void;

  // ── Online flow ──
  setMatchmakingPhase: (p: MatchmakingPhase) => void;
  setCountdownAt: (n: MatchState['countdownAt']) => void;
  setMatchSource: (s: MatchSource) => void;
  applyMatchFound: (payload: MatchFound) => void;
  applyOpponentStep: (step: number, pose: ClimberPose) => void;
  applyOpponentFall: (step: number) => void;
  /** Snap the local climber to the server's authoritative step when the server
   * places us LOWER than our optimistic position (e.g. a disrupt color-swap
   * that reached the server before us made a tap we counted as a hit register
   * as a miss). Prevents the climber from stalling "finished" at a top the
   * server never credited. */
  reconcileYourStep: (step: number, fell: boolean) => void;
  applyOpponentDisrupt: (reshuffleIndices?: number[], reshuffleColors?: ColorType[]) => void;
  cancelMyDisrupt: () => void;
  notifyMyDisruptAbsorbed: () => void;
  applyOpponentShield: () => void;
  setOver: (over: MatchOver) => void;
  setServerSummary: (summary: ServerMatchSummary | null) => void;

  // ── Team flow ──
  beginTeamPending: (teamMode: TeamMode) => void;
  applyTeamFound: (payload: TeamMatchFound, myId: string) => void;
  applyTeamStep: (payload: TeamMatchStep, myId: string) => void;
  applyTeamFall: (payload: TeamMatchFall, myId: string) => void;
  applyTeamSkill: (payload: TeamSkillFired, myId: string) => void;
  applyTeamOver: (payload: TeamMatchOver, myId: string) => void;
}

/** Combo thresholds that UNLOCK skill casting, per sub-mode. attack-defense has
 * no combo gate (the attacker may always cast). */
function computeUnlocks(mode: TeamMode | null, combo: number): { shield: boolean; disrupt: boolean } {
  if (mode === 'alternating') {
    return { shield: combo >= ALT_SHIELD_COMBO, disrupt: combo >= ALT_DISRUPT_COMBO };
  }
  if (mode === 'split-color') {
    const ok = combo >= SPLIT_SKILL_COMBO;
    return { shield: ok, disrupt: ok };
  }
  // attack-defense: gating is by role, not combo.
  return { shield: true, disrupt: true };
}

let floatIdSeq = 0;
let burstIdSeq = 0;

// Debounce pose-reset so rapid taps don't flicker climb→idle→climb. We track
// the latest timeout id per side and cancel the previous one on every tap.
let yourIdleTimer: number | null = null;
let rivalIdleTimer: number | null = null;
// Timer that flips the player's seq to the disrupted colors after the warning
// flash. Stored at module scope so a shield-cancellation can clear it.
let yourDisruptApplyTimer: number | null = null;
// Caster-side timer that locally swaps the rival lane's colors for visual
// feedback when I fire disrupt. Stored so we can abort it when the server
// reports the disrupt was absorbed/cancelled by the opponent's shield.
let rivalDisruptApplyTimer: number | null = null;

const EMPTY_EQUIPPED: EquippedSet = {
  victoryShout: null,
  avatarFrame: null,
  endgameFx: null,
  matchBackground: null,
};

const baseResetFields = {
  yourStep: 0,
  rivalStep: 0,
  yourPose: 'idle' as ClimberPose,
  rivalPose: 'climb' as ClimberPose,
  combo: 0,
  maxCombo: 0,
  timer: 0,
  // Wall-clock anchor (ms) for the race timer. Null until the race starts; the
  // displayed timer is then (now - raceStartMs), NOT an accumulating tick, so a
  // throttled setInterval on one device can't drift it faster/slower than the
  // other player's clock.
  raceStartMs: null as number | null,
  paused: false,
  over: null as MatchOver,
  serverSummary: null as ServerMatchSummary | null,
  shield: false,
  shieldCd: 0,
  vortex: false,
  disruptCd: 0,
  yourDisruptingIndices: [] as number[],
  rivalDisruptingIndices: [] as number[],
  flashWrong: false,
  pressedColor: null as ColorType | null,
  floaties: [] as FloatText[],
  bursts: [] as BurstFx[],
};

const teamResetFields = {
  isTeam: false,
  teamMode: null as TeamMode | null,
  yourSide: null as TeamSide | null,
  turnPlayerId: null as string | null,
  myRole: null as TeamRole | null,
  myOwnedColors: null as ColorType[] | null,
  yourTeam: [] as TeamMember[],
  rivalTeam: [] as TeamMember[],
  shieldUnlocked: false,
  disruptUnlocked: false,
};

// The default starter character (transparent art) is the universal fallback for
// any character-less climber — see @/characters/starterSkin. Re-exported here
// for the existing call sites that import it from the match store.
export { loadStarterSkin };

export const useMatchStore = create<MatchState>((set, get) => ({
  ...teamResetFields,
  teamPending: null,

  mode: 'offline',
  matchId: null,
  matchSource: null,
  warId: null,
  challengeRun: null,
  challengeResult: null,
  youId: null,
  rivalId: null,

  matchmakingPhase: 'idle',
  countdownAt: null,

  rivalName: 'K.NAKA',
  rivalMmr: 1311,
  rivalAvatarUrl: null,

  yourEquipped: { ...EMPTY_EQUIPPED },
  rivalEquipped: { ...EMPTY_EQUIPPED },
  yourClanTag: null,
  yourClanEmblem: null,
  rivalClanTag: null,
  rivalClanEmblem: null,
  yourCharacter: null,
  rivalCharacter: null,

  yourSeq: randomSeq(STEPS_TOTAL),
  rivalSeq: randomSeq(STEPS_TOTAL),
  yourPath: genPath(STEPS_TOTAL),
  rivalPath: genPath(STEPS_TOTAL),

  ...baseResetFields,

  reset: () => {
    if (yourIdleTimer != null) window.clearTimeout(yourIdleTimer);
    if (rivalIdleTimer != null) window.clearTimeout(rivalIdleTimer);
    yourIdleTimer = null;
    rivalIdleTimer = null;
    set({
      mode: 'offline',
      matchId: null,
      youId: null,
      rivalId: null,
      // Clear the room/queue origin too — a stale 'room' here makes the next
      // matchmaking skip QUEUE_JOIN (it waits for a room orchestrator that no
      // longer exists), so Quick Match never finds anyone, not even a bot.
      matchSource: null,
      warId: null,
      challengeRun: null,
      challengeResult: null,
      matchmakingPhase: 'idle',
      countdownAt: null,
      ...teamResetFields,
      teamPending: null,
      ...baseResetFields,
    });
  },

  startLocalMatch: (opts) => {
    const seed = opts?.seed;
    // Offline = AI bot. Mirror the server convention: same color sequence on
    // both lanes for fairness; paths can differ so the two walls don't look
    // identical.
    const sharedSeq = randomSeq(STEPS_TOTAL, seed);
    set({
      mode: 'offline',
      matchId: `local-${Date.now()}`,
      youId: 'local-you',
      rivalId: 'local-rival',
      matchmakingPhase: 'racing',
      countdownAt: null,
      rivalName: opts?.rivalName ?? 'K.NAKA',
      rivalMmr: opts?.rivalMmr ?? 1311,
      yourEquipped: opts?.yourEquipped ?? { ...EMPTY_EQUIPPED },
      rivalEquipped: { ...EMPTY_EQUIPPED },
      // Bot wears the admin-set default starter character (transparent art).
      // Use the warm cache now; otherwise null this match and the async load
      // below fills it in. Never the bare default PNG (which tints into a box).
      rivalCharacter: peekStarterSkin(),
      yourSeq: sharedSeq,
      rivalSeq: [...sharedSeq],
      yourPath: genPath(STEPS_TOTAL, seed),
      rivalPath: genPath(STEPS_TOTAL, seed != null ? seed + 100 : undefined),
      ...baseResetFields,
    });
    // First offline match before the cache warms: fetch the starter, then apply
    // it if we're still in this offline match and nothing else set a character.
    if (!peekStarterSkin()) {
      void loadStarterSkin().then((skin) => {
        if (!skin) return;
        const s = get();
        if (s.mode === 'offline' && !s.rivalCharacter) set({ rivalCharacter: skin });
      });
    }
  },

  startChallengeMatch: (run, youId) => {
    const seq = randomSeq(run.steps, run.seed);
    const path = genPath(run.steps, run.seed);
    set({
      mode: 'offline',
      matchId: `challenge-${run.code}`,
      matchSource: 'challenge',
      warId: null,
      challengeRun: run,
      challengeResult: null,
      youId,
      rivalId: run.creator.id,
      rivalName: run.creator.name,
      rivalMmr: run.creator.mmr,
      rivalAvatarUrl: run.creator.avatarUrl,
      matchmakingPhase: 'countdown',
      countdownAt: 3,
      isTeam: false,
      teamPending: null,
      yourSeq: seq,
      rivalSeq: [...seq],
      yourPath: path,
      rivalPath: [...path],
      ...baseResetFields,
    });
    set({ matchmakingPhase: 'countdown', countdownAt: 3 });
  },

  setChallengeResult: (result) => set({ challengeResult: result }),

  tick: (dt) => {
    const s = get();
    if (s.paused || s.over) return;
    // Anchor on the first racing tick, then read the timer off the wall clock so
    // it can't drift with a throttled interval (the cause of two players' clocks
    // diverging). Cooldowns stay dt-based — they're local and not shared.
    const startMs = s.raceStartMs ?? Date.now();
    set({
      raceStartMs: startMs,
      timer: (Date.now() - startMs) / 1000,
      disruptCd: Math.max(0, s.disruptCd - dt),
      shieldCd: Math.max(0, s.shieldCd - dt),
    });
  },

  setPaused: (v) => set({ paused: v }),
  setPressedColor: (c) => set({ pressedColor: c }),

  tap: (color) => {
    const s = get();
    if (s.matchmakingPhase !== 'racing' || s.paused || s.over) return 'noop';

    const target = s.yourSeq[s.yourStep];
    if (color === target) {
      const nextStep = s.yourStep + 1;
      const newCombo = s.combo + 1;
      const newMax = Math.max(s.maxCombo, newCombo);
      // Length-of-truth is whatever sequence the server (or local match)
      // actually generated — STEPS_TOTAL is just a build-time fallback. The
      // admin can change `match.stepsTotal` at runtime, so capping by it
      // would freeze the climber mid-match.
      const total = s.yourSeq.length;
      const won = nextStep >= total;
      // In online mode, server authoritatively decides 'win'/'lose' via match.over;
      // we still flip yourPose/yourStep optimistically.
      set({
        yourStep: Math.min(nextStep, total),
        yourPose: won ? 'victory' : 'climb',
        combo: newCombo,
        maxCombo: newMax,
        over: s.mode === 'offline' && won ? 'win' : s.over,
      });
      get().pushBurst(color, 'left');
      if (newCombo >= 3) get().pushFloat(`×${newCombo}`, 'var(--c-yellow)', 'left');
      // Debounce: cancel any pending idle-reset so back-to-back correct taps
      // stay in `climb` pose instead of flickering through idle each time.
      if (yourIdleTimer != null) window.clearTimeout(yourIdleTimer);
      yourIdleTimer = window.setTimeout(() => {
        yourIdleTimer = null;
        if (!get().over && get().yourPose !== 'fall' && get().yourPose !== 'victory') {
          set({ yourPose: 'idle' });
        }
      }, CORRECT_TAP_POSE_RESET_MS);
      return 'correct';
    }

    if (s.shield) {
      get().pushFloat('SHIELD!', 'var(--c-cyan)', 'left');
      return 'shielded';
    }

    const dropped = Math.max(0, s.yourStep - 1);
    set({
      yourPose: 'fall',
      yourStep: dropped,
      flashWrong: true,
      combo: 0,
    });
    get().pushFloat('MISS ↓', 'var(--c-red)', 'left');
    setTimeout(() => get().clearFlashWrong(), WRONG_TAP_FLASH_MS);
    // A fall is a hard reset — kill any pending idle-revert and schedule a
    // fresh one so the climber stays in 'fall' for the full WRONG_TAP window.
    if (yourIdleTimer != null) window.clearTimeout(yourIdleTimer);
    yourIdleTimer = window.setTimeout(() => {
      yourIdleTimer = null;
      if (!get().over) set({ yourPose: 'idle' });
    }, WRONG_TAP_POSE_RESET_MS);
    return 'wrong';
  },

  rivalAdvance: () => {
    const s = get();
    if (s.paused || s.over) return;
    const next = s.rivalStep + 1;
    const total = s.rivalSeq.length;
    const won = next >= total;
    set({
      rivalStep: Math.min(next, total),
      rivalPose: won ? 'victory' : 'climb',
      over: s.mode === 'offline' && won ? 'lose' : s.over,
    });
    get().pushBurst('blue', 'right');
    if (rivalIdleTimer != null) window.clearTimeout(rivalIdleTimer);
    rivalIdleTimer = window.setTimeout(() => {
      rivalIdleTimer = null;
      const cur = get();
      if (!cur.over && cur.rivalPose !== 'fall' && cur.rivalPose !== 'victory') {
        set({ rivalPose: 'idle' });
      }
    }, 360);
  },

  fireDisrupt: () => {
    const s = get();
    if (s.matchmakingPhase !== 'racing' || s.disruptCd > 0 || s.over) return false;

    // Flash the 3 next rival holds BEFORE swapping their colors. In offline
    // mode we own the seq locally; in online mode the server has already
    // committed the swap (we mirror it locally for the visual).
    const start = s.rivalStep;
    const indices: number[] = [];
    for (let i = 0; i < DISRUPT_RESHUFFLE_COUNT; i++) {
      const idx = start + i;
      if (idx < s.rivalSeq.length) indices.push(idx);
    }

    // Clear any prior caster-side disrupt timer (defensive).
    if (rivalDisruptApplyTimer != null) {
      window.clearTimeout(rivalDisruptApplyTimer);
      rivalDisruptApplyTimer = null;
    }
    set({
      vortex: true,
      disruptCd: SKILL_DISRUPT_CD_S,
      rivalDisruptingIndices: indices,
    });
    get().pushFloat('DISRUPT!', 'var(--c-purple)', 'right');
    setTimeout(() => get().clearVortex(), DISRUPT_VORTEX_MS);

    // After the warning flash, commit the actual color swap and clear the
    // flashing indices so the scene stops strobing. We mutate the caster's
    // local rival-seq EVEN in online mode — the server has its own truth for
    // the opponent's actual sequence, but the caster never taps those colors,
    // so a locally-shuffled view gives instant "skill landed" feedback without
    // touching the gameplay-authoritative state.
    rivalDisruptApplyTimer = window.setTimeout(() => {
      rivalDisruptApplyTimer = null;
      if (get().over) return;
      const cur = get();
      const seq = [...cur.rivalSeq];
      const slice = indices
        .map((i) => seq[i]!)
        .filter(Boolean)
        .sort(() => Math.random() - 0.5);
      // Force at least one visible change so the caster always sees feedback —
      // a random shuffle can land on the original permutation.
      if (slice.length >= 2 && indices.every((idx, i) => seq[idx] === slice[i])) {
        [slice[0], slice[1]] = [slice[1]!, slice[0]!];
      }
      indices.forEach((idx, i) => {
        if (slice[i]) seq[idx] = slice[i]!;
      });
      set({ rivalSeq: seq, rivalDisruptingIndices: [] });
    }, DISRUPT_FLASH_MS);

    return true;
  },

  fireShield: () => {
    const s = get();
    if (s.matchmakingPhase !== 'racing' || s.shieldCd > 0 || s.over) return false;
    set({ shield: true, shieldCd: SKILL_SHIELD_CD_S });
    get().pushFloat('SHIELD!', 'var(--c-cyan)', 'left');
    setTimeout(() => get().clearShield(), SHIELD_DURATION_MS);
    return true;
  },

  clearShield: () => set({ shield: false }),
  clearVortex: () => set({ vortex: false }),
  clearFlashWrong: () => set({ flashWrong: false }),

  pushFloat: (text, color, side) => {
    const id = ++floatIdSeq;
    set((s) => ({ floaties: [...s.floaties, { id, text, color, side }] }));
    setTimeout(() => get().popFloat(id), 900);
  },
  popFloat: (id) =>
    set((s) => ({ floaties: s.floaties.filter((f) => f.id !== id) })),

  pushBurst: (color, side) => {
    const id = ++burstIdSeq;
    set((s) => ({ bursts: [...s.bursts, { id, color, side }] }));
    setTimeout(() => get().popBurst(id), 500);
  },
  popBurst: (id) =>
    set((s) => ({ bursts: s.bursts.filter((b) => b.id !== id) })),

  setYourPose: (p) => set({ yourPose: p }),
  setRivalPose: (p) => set({ rivalPose: p }),

  // ── Online flow ────────────────────────────────────────────────────
  setMatchmakingPhase: (p) => set({ matchmakingPhase: p }),
  setCountdownAt: (n) => set({ countdownAt: n }),
  setMatchSource: (s) => set({ matchSource: s }),

  applyMatchFound: (payload) => {
    // Warm character PNGs during the countdown so they're ready before the
    // climber renders — avoids first-appearance pop-in over mobile networks.
    preloadCharacterTextures(payload.you.character);
    preloadCharacterTextures(payload.rival.character);
    set({
      mode: 'online',
      matchId: payload.matchId,
      youId: payload.you.id,
      rivalId: payload.rival.id,
      rivalName: payload.rival.name,
      rivalMmr: payload.rival.mmr,
      rivalAvatarUrl: payload.rival.avatarUrl ?? null,
      yourEquipped: payload.you.equipped ?? { ...EMPTY_EQUIPPED },
      rivalEquipped: payload.rival.equipped ?? { ...EMPTY_EQUIPPED },
      yourCharacter: payload.you.character ?? null,
      rivalCharacter: payload.rival.character ?? null,
      yourClanTag: payload.you.clanTag ?? null,
      yourClanEmblem: payload.you.clanEmblem ?? null,
      rivalClanTag: payload.rival.clanTag ?? null,
      rivalClanEmblem: payload.rival.clanEmblem ?? null,
      yourSeq: [...payload.seq.you],
      rivalSeq: [...payload.seq.rival],
      yourPath: [...payload.paths.you],
      rivalPath: [...payload.paths.rival],
      matchmakingPhase: 'reveal',
      ...baseResetFields,
    });
  },

  applyOpponentStep: (step, pose) => {
    set((s) => {
      const next = {
        rivalStep: Math.min(step, s.rivalSeq.length),
        rivalPose: pose,
      } as Partial<MatchState>;
      if (step > s.rivalStep) {
        // Show a small burst when the rival climbs
        get().pushBurst('blue', 'right');
      }
      return next;
    });
    // Debounce idle-reset so fast back-to-back rival steps don't blink.
    if (rivalIdleTimer != null) window.clearTimeout(rivalIdleTimer);
    rivalIdleTimer = window.setTimeout(() => {
      rivalIdleTimer = null;
      const cur = get();
      if (!cur.over && cur.rivalPose === 'climb') set({ rivalPose: 'idle' });
    }, 360);
  },

  reconcileYourStep: (step, fell) => {
    const s = get();
    if (s.over || s.mode !== 'online') return;
    const total = s.yourSeq.length;
    const clamped = Math.max(0, Math.min(step, total));

    if (!fell) {
      // A normal MATCH_STEP only CONFIRMS forward progress the client already
      // predicted. During a fast tap burst the server runs BEHIND the
      // prediction, so its step-confirms arrive with a value below yourStep —
      // snapping back to those is exactly what caused the climber to "jump up
      // then drop". Never pull the prediction backward on a confirm; only adopt
      // the server step if it somehow ran ahead of us (e.g. reconnect replay).
      if (clamped > s.yourStep) {
        if (yourIdleTimer != null) window.clearTimeout(yourIdleTimer);
        set({ yourStep: clamped, yourPose: 'climb' });
      }
      return;
    }

    // fell === true → the server REJECTED a tap we optimistically counted as a
    // hit. Trust the authoritative position and snap back so we keep climbing
    // from the real step instead of idling at a phantom top that stalls the
    // match. (A wrong tap is the ONLY reason the server moves us downward.)
    if (clamped >= s.yourStep) return;
    if (yourIdleTimer != null) window.clearTimeout(yourIdleTimer);
    set({ yourStep: clamped, yourPose: 'fall', combo: 0 });
    yourIdleTimer = window.setTimeout(() => {
      yourIdleTimer = null;
      if (!get().over && get().yourPose !== 'victory') set({ yourPose: 'idle' });
    }, WRONG_TAP_POSE_RESET_MS);
  },

  applyOpponentFall: (step) => {
    set({ rivalStep: Math.max(0, step), rivalPose: 'fall' });
    get().pushFloat('MISS ↓', 'var(--c-red)', 'right');
    if (rivalIdleTimer != null) window.clearTimeout(rivalIdleTimer);
    rivalIdleTimer = window.setTimeout(() => {
      rivalIdleTimer = null;
      const cur = get();
      if (!cur.over) set({ rivalPose: 'idle' });
    }, 820);
  },

  applyOpponentDisrupt: (reshuffleIndices, reshuffleColors) => {
    // Opponent fired DISRUPT on me. Flash the affected holds first so the
    // player sees the warning, then paint the colors the server actually
    // assigned — using a local random shuffle would diverge from the server's
    // truth, every following tap would be the wrong color, and the match
    // would stall mid-climb.
    if (!reshuffleIndices?.length) return;
    // Clear any previous pending disrupt timer (defensive — disrupt CD
    // normally prevents stacking, but a network reorder shouldn't leak).
    if (yourDisruptApplyTimer != null) {
      window.clearTimeout(yourDisruptApplyTimer);
      yourDisruptApplyTimer = null;
    }
    set({
      vortex: true,
      yourDisruptingIndices: [...reshuffleIndices],
    });
    get().pushFloat('DISRUPTED!', 'var(--c-purple)', 'left');
    setTimeout(() => get().clearVortex(), DISRUPT_VORTEX_MS);

    yourDisruptApplyTimer = window.setTimeout(() => {
      yourDisruptApplyTimer = null;
      if (get().over) return;
      set((s) => {
        const seq = [...s.yourSeq];
        if (reshuffleColors && reshuffleColors.length === reshuffleIndices.length) {
          reshuffleIndices.forEach((idx, i) => {
            const c = reshuffleColors[i];
            if (idx < seq.length && c) seq[idx] = c;
          });
        } else {
          const slice = reshuffleIndices.map((i) => seq[i]!).filter(Boolean);
          const shuffled = [...slice].sort(() => Math.random() - 0.5);
          reshuffleIndices.forEach((idx, i) => {
            if (idx < seq.length && shuffled[i]) seq[idx] = shuffled[i]!;
          });
        }
        // In split-color 2v2, the hold owner may have changed after the
        // reshuffle — recompute who should tap next so the correct player's
        // buttons are enabled and the other's are disabled.
        let turnPlayerId = s.turnPlayerId;
        if (s.teamMode === 'split-color' && s.yourTeam.length > 0) {
          const nextColor = seq[s.yourStep];
          if (nextColor) {
            const owner = s.yourTeam.find((m) => m.ownedColors?.includes(nextColor));
            if (owner) turnPlayerId = owner.id;
          }
        }
        return { yourSeq: seq, yourDisruptingIndices: [], turnPlayerId };
      });
    }, DISRUPT_FLASH_MS);
  },

  /** Shield I just cast cancelled an incoming disrupt mid-flash. Abort the
   * pending color swap and clear the warning UI. */
  cancelMyDisrupt: () => {
    if (yourDisruptApplyTimer != null) {
      window.clearTimeout(yourDisruptApplyTimer);
      yourDisruptApplyTimer = null;
    }
    set({ yourDisruptingIndices: [], vortex: false });
    get().pushFloat('ABSORBED!', 'var(--c-cyan)', 'left');
  },

  /** Disrupt I cast was absorbed/cancelled by the opponent's shield. Abort
   * the caster-side local visual (rival lane flash + pending color swap) so
   * I see the dodge clearly instead of watching the colors change anyway. */
  notifyMyDisruptAbsorbed: () => {
    if (rivalDisruptApplyTimer != null) {
      window.clearTimeout(rivalDisruptApplyTimer);
      rivalDisruptApplyTimer = null;
    }
    set({ rivalDisruptingIndices: [], vortex: false });
    get().pushFloat('ABSORBED!', 'var(--c-cyan)', 'right');
  },

  applyOpponentShield: () => {
    // Opponent cast Shield — no effect on me, but show a small float on their lane.
    get().pushFloat('SHIELD!', 'var(--c-cyan)', 'right');
  },

  setOver: (over) => set({ over }),
  setServerSummary: (summary) => set({ serverSummary: summary }),

  // ── Team flow ──────────────────────────────────────────────────────
  beginTeamPending: (teamMode) => set({ teamPending: teamMode, matchSource: 'room' }),

  applyTeamFound: (payload, myId) => {
    const me = payload.yourTeam.find((m) => m.id === myId) ?? payload.yourTeam[0]!;
    const rival0 = payload.rivalTeam[0]!;

    // Initial tapper for MY lane (before the first server step event).
    let turn: string | null = null;
    if (payload.teamMode === 'attack-defense') {
      turn = payload.yourTeam.find((m) => m.role === 'climber')?.id ?? me.id;
    } else if (payload.teamMode === 'alternating') {
      turn = payload.yourTeam.find((m) => m.order === 0)?.id ?? me.id;
    } else {
      const first = payload.seq.you[0];
      turn = payload.yourTeam.find((m) => m.ownedColors?.includes(first!))?.id ?? me.id;
    }

    // The result screen celebrates both winners in 2v2. Preload every member's
    // animation frames now so the second character never pops in late.
    [...payload.yourTeam, ...payload.rivalTeam].forEach((member) => {
      preloadCharacterTextures(member.character ?? null);
    });

    set({
      mode: 'online',
      isTeam: true,
      teamPending: null,
      teamMode: payload.teamMode,
      yourSide: payload.yourSide,
      matchId: payload.matchId,
      youId: myId,
      rivalId: rival0.id,
      myRole: me.role ?? null,
      myOwnedColors: me.ownedColors ?? null,
      yourTeam: payload.yourTeam,
      rivalTeam: payload.rivalTeam,
      turnPlayerId: turn,
      shieldUnlocked: payload.teamMode === 'attack-defense',
      disruptUnlocked: payload.teamMode === 'attack-defense',
      rivalName: payload.rivalTeam.map((m) => m.name).join(' & '),
      rivalMmr: Math.round(
        payload.rivalTeam.reduce((s, m) => s + m.mmr, 0) / payload.rivalTeam.length,
      ),
      rivalAvatarUrl: rival0.avatarUrl ?? null,
      yourClanTag: me.clanTag ?? null,
      yourClanEmblem: me.clanEmblem ?? null,
      rivalClanTag: rival0.clanTag ?? null,
      rivalClanEmblem: rival0.clanEmblem ?? null,
      yourEquipped: me.equipped ?? { ...EMPTY_EQUIPPED },
      rivalEquipped: rival0.equipped ?? { ...EMPTY_EQUIPPED },
      yourCharacter: me.character ?? null,
      rivalCharacter: rival0.character ?? null,
      yourSeq: [...payload.seq.you],
      rivalSeq: [...payload.seq.rival],
      yourPath: [...payload.paths.you],
      rivalPath: [...payload.paths.rival],
      matchmakingPhase: 'reveal',
      ...baseResetFields,
    });
  },

  applyTeamStep: (payload, _myId) => {
    const s = get();
    const total = payload.side === s.yourSide ? s.yourSeq.length : s.rivalSeq.length;
    const won = payload.step >= total;
    if (payload.side === s.yourSide) {
      const unlocks = computeUnlocks(s.teamMode, payload.combo);
      set({
        yourStep: Math.min(payload.step, total),
        yourPose: won ? 'victory' : payload.pose,
        combo: payload.combo,
        maxCombo: Math.max(s.maxCombo, payload.combo),
        turnPlayerId: payload.nextTurnId ?? s.turnPlayerId,
        // Latch: once the combo threshold unlocks a skill it stays unlocked for
        // the rest of the match. A later combo reset must not re-lock it; usage
        // is then gated only by remaining inventory charges.
        shieldUnlocked: s.shieldUnlocked || unlocks.shield,
        disruptUnlocked: s.disruptUnlocked || unlocks.disrupt,
      });
      get().pushBurst('red', 'left');
      if (payload.combo >= 3) get().pushFloat(`×${payload.combo}`, 'var(--c-yellow)', 'left');
      if (yourIdleTimer != null) window.clearTimeout(yourIdleTimer);
      yourIdleTimer = window.setTimeout(() => {
        yourIdleTimer = null;
        if (!get().over && get().yourPose === 'climb') set({ yourPose: 'idle' });
      }, CORRECT_TAP_POSE_RESET_MS);
    } else {
      set({ rivalStep: Math.min(payload.step, total), rivalPose: won ? 'victory' : payload.pose });
      get().pushBurst('blue', 'right');
      if (rivalIdleTimer != null) window.clearTimeout(rivalIdleTimer);
      rivalIdleTimer = window.setTimeout(() => {
        rivalIdleTimer = null;
        if (!get().over && get().rivalPose === 'climb') set({ rivalPose: 'idle' });
      }, 360);
    }
  },

  applyTeamFall: (payload, _myId) => {
    const s = get();
    if (payload.side === s.yourSide) {
      set({
        yourStep: Math.max(0, payload.step),
        yourPose: 'fall',
        combo: 0,
        // Combo resets, but skill unlocks are a permanent per-match latch —
        // do not re-lock here. Casting stays gated by inventory charges.
        flashWrong: true,
        turnPlayerId: payload.nextTurnId ?? s.turnPlayerId,
      });
      get().pushFloat('MISS ↓', 'var(--c-red)', 'left');
      setTimeout(() => get().clearFlashWrong(), WRONG_TAP_FLASH_MS);
      if (yourIdleTimer != null) window.clearTimeout(yourIdleTimer);
      yourIdleTimer = window.setTimeout(() => {
        yourIdleTimer = null;
        if (!get().over) set({ yourPose: 'idle' });
      }, WRONG_TAP_POSE_RESET_MS);
    } else {
      set({ rivalStep: Math.max(0, payload.step), rivalPose: 'fall' });
      get().pushFloat('MISS ↓', 'var(--c-red)', 'right');
      if (rivalIdleTimer != null) window.clearTimeout(rivalIdleTimer);
      rivalIdleTimer = window.setTimeout(() => {
        rivalIdleTimer = null;
        if (!get().over) set({ rivalPose: 'idle' });
      }, 820);
    }
  },

  applyTeamSkill: (payload, _myId) => {
    const s = get();
    const castByMyTeam = payload.side === s.yourSide;
    const p = payload.payload;
    if (payload.skill === 'disrupt') {
      if (castByMyTeam) {
        // We disrupted the enemy lane.
        if (p?.absorbedBy === 'shield') {
          get().notifyMyDisruptAbsorbed();
          return;
        }
        const indices = p?.reshuffleIndices ?? [];
        if (rivalDisruptApplyTimer != null) window.clearTimeout(rivalDisruptApplyTimer);
        set({ vortex: true, rivalDisruptingIndices: [...indices] });
        get().pushFloat('DISRUPT!', 'var(--c-purple)', 'right');
        setTimeout(() => get().clearVortex(), DISRUPT_VORTEX_MS);
        rivalDisruptApplyTimer = window.setTimeout(() => {
          rivalDisruptApplyTimer = null;
          if (get().over) return;
          set((cur) => {
            const seq = [...cur.rivalSeq];
            if (p?.reshuffleColors && p.reshuffleColors.length === indices.length) {
              indices.forEach((idx, i) => { const c = p.reshuffleColors![i]; if (c && idx < seq.length) seq[idx] = c; });
            }
            return { rivalSeq: seq, rivalDisruptingIndices: [] };
          });
        }, DISRUPT_FLASH_MS);
      } else {
        // Enemy disrupted us.
        if (p?.absorbedBy === 'shield') return;
        get().applyOpponentDisrupt(p?.reshuffleIndices, p?.reshuffleColors);
      }
    } else {
      // Shield.
      if (castByMyTeam) {
        if (p?.cancelledDisruptIndices?.length) get().cancelMyDisrupt();
        const dur = SHIELD_DURATION_MS * (payload.enhanced ? TEAM_SHIELD_BONUS_MULT : 1);
        set({ shield: true });
        get().pushFloat(payload.enhanced ? 'SHIELD+!' : 'SHIELD!', 'var(--c-cyan)', 'left');
        setTimeout(() => get().clearShield(), dur);
      } else {
        get().applyOpponentShield();
        if (p?.cancelledDisruptIndices?.length) get().notifyMyDisruptAbsorbed();
      }
    }
  },

  applyTeamOver: (payload, myId) => {
    const youWon = payload.winnerSide === get().yourSide;
    set({
      serverSummary: {
        winnerId: youWon ? myId : (get().rivalId ?? ''),
        finalSteps: payload.finalSteps,
        durationMs: payload.durationMs,
        mmrDelta: { you: payload.mmrDelta, rival: 0 },
        rewards: payload.rewards,
        rankProgression: payload.rankProgression ?? null,
        winnerShout: payload.winnerShout ?? null,
        winnerShouts: payload.winnerShouts ?? (payload.winnerShout ? [payload.winnerShout] : []),
      },
      over: youWon ? 'win' : 'lose',
    });
  },
}));
